import { useCallback, useRef, useState } from 'react';
import toast from 'react-hot-toast';

export interface CopyToClipboardOptions {
  onSuccess?: (text: string) => void;
  onError?: (error: Error) => void;
  showToast?: boolean;
  successMessage?: string;
  errorMessage?: string;
  feedbackDuration?: number;
}

export interface CopyToClipboardResult {
  copyToClipboard: (text: string) => Promise<boolean>;
  isCopying: boolean;
  isRecentlyCopied: boolean;
  copiedText: string | null;
}

export const useCopyToClipboard = (options: CopyToClipboardOptions = {}): CopyToClipboardResult => {
  const {
    onSuccess,
    onError,
    showToast = false,
    successMessage = 'Copied to clipboard',
    errorMessage = 'Failed to copy to clipboard',
    feedbackDuration = 2000,
  } = options;

  const [isCopying, setIsCopying] = useState(false);
  const [isRecentlyCopied, setIsRecentlyCopied] = useState(false);
  const [copiedText, setCopiedText] = useState<string | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const copyWithModernAPI = useCallback(async (text: string): Promise<boolean> => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (error) {
      return false;
    }
  }, []);

  const copyWithFallback = useCallback((text: string): boolean => {
    const textarea = document.createElement('textarea');
    
    // Configure textarea for invisible copying
    Object.assign(textarea.style, {
      position: 'fixed',
      left: '-9999px',
      top: '0',
      width: '1px',
      height: '1px',
      border: 'none',
      outline: 'none',
      background: 'transparent',
      fontSize: '16px', // Prevent zoom on iOS
    });
    
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.setAttribute('contenteditable', 'true');
    
    document.body.appendChild(textarea);
    
    // Select text
    textarea.focus();
    textarea.select();
    textarea.setSelectionRange(0, text.length);
    
    let success = false;
    try {
      success = document.execCommand('copy');
    } catch (error) {
      success = false;
    } finally {
      document.body.removeChild(textarea);
    }
    
    return success;
  }, []);

  const copyToClipboard = useCallback(async (text: string): Promise<boolean> => {
    if (!text || typeof text !== 'string') {
      const error = new Error('Invalid text provided for copying');
      onError?.(error);
      if (showToast) {
        toast.error(errorMessage);
      }
      return false;
    }

    setIsCopying(true);
    let success = false;

    try {
      // Try modern clipboard API first (requires HTTPS or localhost)
      if (navigator.clipboard && window.isSecureContext) {
        success = await copyWithModernAPI(text);
      }

      // Fallback to execCommand if modern API fails or is unavailable
      if (!success) {
        success = copyWithFallback(text);
      }

      if (success) {
        setCopiedText(text);
        setIsRecentlyCopied(true);
        onSuccess?.(text);
        
        if (showToast) {
          toast.success(successMessage);
        }

        // Clear feedback state after duration
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        
        timeoutRef.current = setTimeout(() => {
          setIsRecentlyCopied(false);
          setCopiedText(null);
        }, feedbackDuration);
        
      } else {
        const error = new Error('Copy operation failed');
        onError?.(error);
        
        if (showToast) {
          toast.error(errorMessage);
        }
      }
    } catch (error) {
      const copyError = error instanceof Error ? error : new Error('Unknown copy error');
      onError?.(copyError);
      
      if (showToast) {
        toast.error(errorMessage);
      }
    } finally {
      setIsCopying(false);
    }

    return success;
  }, [
    copyWithModernAPI,
    copyWithFallback,
    onSuccess,
    onError,
    showToast,
    successMessage,
    errorMessage,
    feedbackDuration,
  ]);

  return {
    copyToClipboard,
    isCopying,
    isRecentlyCopied,
    copiedText,
  };
};
